import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useAuth } from '@/auth/context'

/**
 * "AugRank support is viewing your account" — the S-B11 exit criterion, seen from
 * the **customer's** side, with the staff member's own controls on it.
 *
 * Screen 08 requires an unmistakable in-app banner on an impersonated session, and
 * this is the half that is in the tenant application rather than in the staff
 * console: the borrowed session is a cookie on `app.augrank.com`, so it is *this*
 * app the staff member is looking at, and it is this app that has to say so.
 *
 * **Red, full width, above everything, and there is no way to dismiss it.** No
 * close button, no collapsed state, no `localStorage` flag. It is rendered by
 * `AppShell` outside the routed area, so no screen can omit it and it cannot be
 * scrolled away from.
 *
 * **The banner is drawn for the customer, not for the staff member**, and the
 * wording is chosen accordingly: it says what is happening, that it ends by itself,
 * and where to see the record. It does not say *why* — the reason is in the audit
 * log and in the email they were sent, which are the two places it belongs. A
 * reason quoted in a banner over their own screen would be AugRank explaining
 * itself in the middle of somebody's work.
 *
 * THE CONTROLS ARE HERE TOO, AND THAT IS SF-0's RULING (SUP-38, SUP-81, NC-8)
 * ---------------------------------------------------------------------------
 * Screen 08 draws *"Request write access"* and *"Exit impersonation"* **inside the
 * impersonated tenant view**, over the tenant app frame. They existed only on the
 * console's banner, so a staff member who wanted to end a session had to leave the
 * customer's screen open in one tab, find the console in another, and press the
 * button there. NC-8 ruled that split a gap rather than the design: the controls
 * go where the staff member is looking.
 *
 * **Rendering them on a page the customer can also see changes what they may be
 * built on.** NC-8's security half is explicit — both must be authorised from the
 * support session's own server-issued claims, never from anything the page carries.
 * They are: `POST /api/v1/support-session/{end,write-request}` read the grant off
 * `sessions.impersonation_id`, a column only the server writes, and **take no grant
 * id from the caller**. A customer pressing them has no support session, so the
 * routes answer 404 before reading any state; there is nothing here for them to
 * substitute, and the buttons are drawn only when `impersonation_mode` is set,
 * which the same server sets.
 *
 * *Exit* ends the grant server-side and deletes the borrowed session, so pressing
 * back cannot undo it. *Request write access* records a request and grants nothing
 * — a **different** staff member has to approve it from the console, which the
 * two-person rule enforces in three places.
 *
 * **The countdown is the server's number, not a timer** (SUP-39). This said "within
 * the hour" while the console's banner showed exact minutes — the same session
 * described twice, with the vaguer half on the host where the work happens.
 * `session.impersonation_minutes_left` is derived from the session's absolute
 * expiry on every response, which is the grant's own `expires_at`. A client-side
 * tick would drift from the clock that actually refuses the session, and a banner
 * reading "3 minutes left" over a dead session lies about a security boundary.
 *
 * **It said "in your audit log under Settings" and there is no such screen.** Found
 * on the first live support session, in a browser, by the owner looking for the
 * entry the banner had just promised. **There is one now** — SUP-41's *Audit log*
 * panel, `/settings/audit`, reading the same `GET /api/v1/audit` that has served
 * these entries since S-B12 — so the copy names the route again.
 *
 * `null` on every ordinary session, which is one attribute read.
 */
export function SupportAccessBanner(): JSX.Element | null {
  const { session, refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const mode = session?.impersonation_mode
  const minutes = session?.impersonation_minutes_left
  if (!mode) return null

  async function act(
    path: '/api/v1/support-session/end' | '/api/v1/support-session/write-request',
  ) {
    setBusy(true)
    setNote(null)
    const result = await call(() => api.POST(path))
    if (!result.ok) {
      setNote(result.failure.message)
      setBusy(false)
      return
    }
    // `refresh()` re-reads `/auth/me`, which is what removes this banner after an
    // *Exit*: the session row is gone, so the call comes back anonymous and
    // `AuthProvider` moves the app to the sign-in screen. Nothing here has to
    // know that — the banner is a function of the session, and the session
    // changed.
    setNote(
      result.data.ended
        ? 'Support access ended.'
        : 'Asked. A second member of staff has to approve it.',
    )
    await refresh()
    setBusy(false)
  }

  return (
    <div className="app-support-banner" role="alert">
      <span aria-hidden="true">⚠</span>
      <span>
        <strong>Support is viewing your account</strong> —{' '}
        {mode === 'write' ? (
          <>
            with permission to make changes, approved by a second member of their staff. This ends
            automatically in {countdown(minutes, 'fifteen minutes')}.
          </>
        ) : (
          <>
            read-only. Nothing can be approved, published or changed. This ends automatically in{' '}
            {countdown(minutes, 'the hour')}.
          </>
        )}{' '}
        You were emailed when it started, and both the start and the end are recorded in your
        organisation&rsquo;s audit log under Settings.
      </span>
      {note ? <span className="ar-mono">{note}</span> : null}
      <span className="app-support-banner__spacer" />
      {mode === 'read_only' ? (
        <button
          type="button"
          className="ar-btn ar-btn--ghost ar-btn--sm"
          disabled={busy}
          onClick={() => {
            void act('/api/v1/support-session/write-request')
          }}
        >
          Request write access
        </button>
      ) : null}
      <button
        type="button"
        className="ar-btn ar-btn--ghost ar-btn--sm"
        disabled={busy}
        onClick={() => {
          void act('/api/v1/support-session/end')
        }}
      >
        Exit impersonation
      </button>
    </div>
  )
}

/**
 * The countdown, or the grant's nominal window when there is no number.
 *
 * `null` reaches here only from a session minted before `impersonation_minutes_left`
 * existed, and the fallback is the sentence this banner used to carry — vaguer, and
 * never wrong. Zero is rendered as "under a minute" rather than "0 minutes": a
 * banner is not the place to say a session has already expired when the next
 * request will say so definitively.
 */
function countdown(minutes: number | null | undefined, fallback: string): string {
  if (minutes === null || minutes === undefined) return fallback
  if (minutes <= 0) return 'under a minute'
  return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`
}
